import React, { FunctionComponent } from 'react';
import useHeadingGroupStyles from '@modules/ui/shared/styles/HeadingGroup.styles';
import {
  ReferenceType,
  APIReferenceProperty,
  APIReferenceEvent,
  APIReferenceMethod,
  APIReferenceCallback,
} from '@modules/client';
import APIReferenceDescription from '../../sharedUI/components/APIReferenceDescription';
import type { ClassReference, ParentClassReference } from '../../types';
import CharacteristicsIteratorContainer from './CharacteristicsIteratorContainer';
import ReferenceSummary from '../../sharedUI/components/ReferenceSummary';
import ReferenceSummaryV2 from '../../sharedUI/components/ReferenceSummaryV2';
import MemberDetailsContainer from './MemberDetailsContainer';

export interface ClassContentProps {
  classReference: ClassReference;
  classReferenceParents: ParentClassReference[] | null;
  engineRevampEnabled?: boolean;
}

const ClassContent: FunctionComponent<React.PropsWithChildren<ClassContentProps>> = ({
  classReference,
  classReferenceParents: orderedParentClassReferences,
  engineRevampEnabled = false,
}) => {
  const {
    name,
    description,
    tags,
    properties,
    methods,
    events,
    callbacks,
    codeSamples,
    deprecationMessage,
  } = classReference;
  const {
    classes: { root },
  } = useHeadingGroupStyles();

  if (engineRevampEnabled) {
    return (
      <div className={root}>
        <ReferenceSummaryV2
          referenceType={ReferenceType.CLASS}
          reference={classReference}
          orderedParentReferences={orderedParentClassReferences}
        />
        <APIReferenceDescription
          name={name}
          tags={tags}
          description={description}
          deprecationMessage={deprecationMessage}
          codeSamples={codeSamples}
          engineRevampEnabled
        />
        <MemberDetailsContainer
          properties={properties as APIReferenceProperty[] | null}
          events={events as APIReferenceEvent[] | null}
          methods={methods as APIReferenceMethod[] | null}
          callbacks={callbacks as APIReferenceCallback[] | null}
        />
      </div>
    );
  }

  return (
    <div className={root}>
      <APIReferenceDescription
        name={name}
        tags={tags}
        description={description}
        deprecationMessage={deprecationMessage}
        codeSamples={codeSamples}
      />
      <ReferenceSummary
        referenceType={ReferenceType.CLASS}
        reference={classReference}
        orderedParentReferences={orderedParentClassReferences}
      />
      {/* extended feature descriptions */}
      <CharacteristicsIteratorContainer
        properties={properties}
        events={events}
        methods={methods}
        parents={orderedParentClassReferences}
        callbacks={callbacks}
      />
    </div>
  );
};
export default ClassContent;
